import { Link, useLocation } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useAuth } from '../auth/AuthProvider'
import { useMe, useWorkYard } from '../api/hooks'

/**
 * Mobile bottom tab bar — the phone-first navigation a barn tool needs.
 * Visible only when signed in, only below md (desktop keeps the top nav).
 * Tabs adapt to the person: yard people get Yard/Tasks; owners get Horses/Livery.
 */
export function BottomNav() {
  const { pathname } = useLocation()
  const { t } = useI18n()
  const { user } = useAuth()
  const { data: me } = useMe(!!user)
  const { farmId } = useWorkYard()

  if (!user || !me) return null
  // Keep marketing/auth pages clean.
  if (pathname === '/' || pathname.startsWith('/sign') || pathname === '/features') return null

  const worksAtYard = !!farmId

  const tabs = worksAtYard
    ? [
        { to: '/farm', icon: '🏡', label: t('nav.yard') },
        { to: '/farm/tasks', icon: '✅', label: t('nav.tasks') },
        { to: '/app', icon: '🐎', label: t('nav.myHorses') },
        { to: '/settings', icon: '⚙️', label: t('nav.settings') },
      ]
    : [
        { to: '/app', icon: '🐎', label: t('nav.myHorses') },
        { to: '/my-farm', icon: '📍', label: t('nav.myFarm') },
        { to: '/discover', icon: '🔍', label: t('nav.discover') },
        { to: '/settings', icon: '⚙️', label: t('nav.settings') },
      ]

  const isActive = (to: string) =>
    to === '/farm' || to === '/app'
      ? pathname === to || (to === '/app' && pathname.startsWith('/app/'))
      : pathname === to || pathname.startsWith(to + '/')

  return (
    <>
      {/* Spacer so page content never hides behind the fixed bar */}
      <div className="h-20 md:hidden" aria-hidden />
      <nav
        aria-label={t('nav.menu')}
        className="fixed inset-x-0 bottom-0 z-40 border-t border-brand-100 dark:border-brand-800 bg-brand-50/95 dark:bg-brand-900/95 backdrop-blur md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="mx-auto grid max-w-lg grid-cols-4">
          {tabs.map((tab) => {
            const active = isActive(tab.to)
            return (
              <Link
                key={tab.to}
                to={tab.to}
                aria-current={active ? 'page' : undefined}
                className={`flex min-h-16 flex-col items-center justify-center gap-0.5 px-1 pt-2 pb-1.5 text-center transition ${
                  active
                    ? 'font-bold text-brand-900 dark:text-white'
                    : 'font-medium text-brand-500 dark:text-brand-400'
                }`}
              >
                <span className={`text-xl leading-none ${active ? '' : 'opacity-80'}`} aria-hidden>{tab.icon}</span>
                <span className="text-xs leading-tight">{tab.label}</span>
                {active && <span className="h-1 w-8 rounded-full bg-brand-700 dark:bg-brand-300" aria-hidden />}
              </Link>
            )
          })}
        </div>
      </nav>
    </>
  )
}
